/**
 * Chat Rooms — shared type contracts consumed by backend, frontend, and python-api.
 *
 * Covers: DTOs, Socket.IO event payloads, Python endpoint contracts,
 * and the George virtual participant sentinel.
 */

import { PersonaGrounding } from './audience-runtime';
import { SurveyChoiceLabelMap } from './survey-choice-labels';
import { ParticipantMediaEvidence, MediaPixelObject, MediaPresentation, PersonalRecordAvailability } from './conversation-media';
import { AnswerGroundingLevel } from './answer-grounding';
import { AnalystChartSpec } from './report-analyst';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Sentinel participant ID for George (research assistant). No DB row — virtual only. */
export const GEORGE_PARTICIPANT_ID = '__george__' as const;

/** Sender name of the room display item (the audience chart posted by George). */
export const DISPLAY_SENDER_NAME = 'Insights assistant' as const;

/** Most twins a room can hold (create and add). */
export const MAX_ROOM_PARTICIPANTS = 10;

/** Teller slots per round when the room setting is missing (owner 2026-10-14: 1; saved counts are kept). */
export const DEFAULT_TELLER_COUNT = 1;

/** Most emoji reactions one twin reply may keep. */
export const MAX_REACTIONS_PER_REPLY = 3;

/**
 * The Unicode White_Space set (25 code points; U+200B and U+FEFF are not in it).
 * A room message is empty when nothing is left after stripping exactly these.
 * python-api mirrors it as `models.room_chat.ROOM_WHITE_SPACE`.
 */
export const ROOM_WHITE_SPACE =
  '\u0009\u000a\u000b\u000c\u000d\u0020\u0085\u00a0\u1680\u2000\u2001\u2002\u2003\u2004\u2005\u2006' +
  '\u2007\u2008\u2009\u200a\u2028\u2029\u202f\u205f\u3000';

// ---------------------------------------------------------------------------
// Enums / Unions
// ---------------------------------------------------------------------------

export type ChatRoomStatus = 'preparing' | 'active' | 'closed' | 'error';

export type ChatRoomParticipantEnrichmentStatus =
  | 'ready'
  | 'enriching'
  | 'failed';

/**
 * Room message sender types: the one TypeScript list. The DB enum
 * (`chat_room_sender_type`) and python-api's `RoomSenderType` are bound to it
 * by tests/apps/python-api/test_closed_value_set_parity.py.
 */
export const CHAT_ROOM_SENDER_TYPES = ['user', 'twin', 'george', 'system'] as const;
export type ChatRoomSenderType = (typeof CHAT_ROOM_SENDER_TYPES)[number];

export type ChatRoomMessageMode = 'chat' | 'sample';

/**
 * Room message metadata types: the one TypeScript list. The DB enum
 * (`chat_room_metadata_type`) and python-api's `RoomMetadataType` are bound to it
 * by tests/apps/python-api/test_closed_value_set_parity.py.
 */
export const CHAT_ROOM_METADATA_TYPES = ['sample_artifact', 'system_event', 'audience_plot'] as const;
export type ChatRoomMetadataType = (typeof CHAT_ROOM_METADATA_TYPES)[number];

/** Room message `metadata.replyTo`: the thread message a reactor's text responds to (stored by Nest). */
export interface RoomMessageReplyTo {
  sequenceIndex: number;
}

/** Callback item `replyTo` as Python sends it: a participant's message this round, or the display. */
export interface RoomCallbackReplyTo {
  kind: 'participant' | 'display';
  participant?: string;
}

/** A twin's part in a group round. python-api mirrors it as `RoomRole`. */
export const ROOM_ROLES = ['teller', 'reactor'] as const;
export type RoomRole = (typeof ROOM_ROLES)[number];

/** Declared chart kind of an audience detail column (not `AudienceColumn.valueKind`). */
export const AUDIENCE_CHART_KINDS = ['single', 'multi', 'ranking', 'numeric'] as const;
export type AudienceChartKind = (typeof AUDIENCE_CHART_KINDS)[number];

/**
 * Conversation model options a room owner can pick (`ChatRoomSettings.conversationModel`).
 * Option ids, not model ids: `config/llm-routing.yaml` maps each to its routes. The first
 * entry is the default (an absent value behaves as it). Nest validates against this list;
 * python-api mirrors it as the `conversationModel` Literal on `TwinChatRequest` and
 * `RoomChatRequest` (bound by tests/apps/python-api/test_closed_value_set_parity.py).
 */
// `argonclassic`: TEMPORARY (fast-path round 2 control): remove after round 2.
export const CONVERSATION_MODELS = [
  'luna',
  'haiku',
  'flash',
  'sol',
  'sonnet',
  'opus',
  'argon',
  'blend1',
  'argonclassic',
] as const;
export type ConversationModel = (typeof CONVERSATION_MODELS)[number];
export const DEFAULT_CONVERSATION_MODEL: ConversationModel = CONVERSATION_MODELS[0];

/**
 * Display name of each option in the advanced picker: product names, the same in every locale.
 * Adding an option (a new blend) here, in `CONVERSATION_MODELS` and in `CONVERSATION_MODEL_TIER`
 * is all the picker needs: it lists the options from these three, with no per-option code.
 */
export const CONVERSATION_MODEL_LABELS: Record<ConversationModel, string> = {
  luna: 'Luna',
  haiku: 'Haiku',
  flash: 'Flash',
  sol: 'Sol',
  sonnet: 'Sonnet',
  opus: 'Opus',
  argon: 'Argon',
  blend1: 'Blend 1',
  // TEMPORARY (fast-path round 2 control): staff-only, remove after round 2.
  argonclassic: 'Argon classic',
};

/** Picker groups for the conversation model options, in display order. */
export const CONVERSATION_MODEL_TIERS = ['low', 'medium', 'high', 'blend'] as const;
export type ConversationModelTier = (typeof CONVERSATION_MODEL_TIERS)[number];
/** The picker group of each option; options keep CONVERSATION_MODELS order inside a group. */
export const CONVERSATION_MODEL_TIER: Record<ConversationModel, ConversationModelTier> = {
  luna: 'low',
  haiku: 'low',
  flash: 'medium',
  // Owner 2026-10-15: Sol lists (and bills) under High, so an advanced Sol pick costs the same
  // as the High level, which runs Sol.
  sol: 'high',
  sonnet: 'medium',
  opus: 'high',
  argon: 'high',
  // Owner 2026-10-14: Blend 1 is listed (and billed) under Medium. `blend` stays for future
  // blends; pickers skip a group with no options.
  blend1: 'medium',
  // TEMPORARY (fast-path round 2 control): remove after round 2. The pre-2026-10-15 High (all
  // five content routes on Argon); listed and billed under High like `argon`.
  argonclassic: 'high',
};

/**
 * Internal options: visible to and settable by verified Fairgen staff only, whatever the org's
 * `conversationModel` flag says (Nest gates the settings write, the turn path and the picker
 * payload). No customer i18n label and no level points at them.
 * TEMPORARY (fast-path round 2 control): `argonclassic` is the round 2 control arm; remove after round 2.
 */
export const CONVERSATION_MODEL_INTERNAL_OPTIONS = ['argonclassic'] as const satisfies ReadonlyArray<ConversationModel>;
export type InternalConversationModel = (typeof CONVERSATION_MODEL_INTERNAL_OPTIONS)[number];

/**
 * Auth payload key (`user.featureFlags`) Nest sets to whether the viewer may see and pick
 * `CONVERSATION_MODEL_INTERNAL_OPTIONS` (verified Fairgen staff only). Not a registry flag:
 * no organization or workspace override can open it.
 */
export const CONVERSATION_MODEL_INTERNAL_FLAG = 'conversationModelInternal';

/** True when `option` is staff-only (`CONVERSATION_MODEL_INTERNAL_OPTIONS`). */
export function isInternalConversationModel(option: string | null | undefined): option is InternalConversationModel {
  if (!option) {
    return false;
  }
  return (CONVERSATION_MODEL_INTERNAL_OPTIONS as ReadonlyArray<string>).includes(option);
}

/**
 * Conversation levels every room owner picks from in the composer (`ChatRoomSettings.conversationLevel`).
 * Missing = `DEFAULT_CONVERSATION_LEVEL`. Levels never reach Python: Nest resolves a level to an
 * option id through `CONVERSATION_LEVEL_MODEL`, so re-pointing a level is a one-line change here
 * and existing rooms follow.
 */
export const CONVERSATION_LEVELS = ['low', 'medium', 'high'] as const;
export type ConversationLevel = (typeof CONVERSATION_LEVELS)[number];
/**
 * What a MISSING level means: Low. Rooms created before levels shipped carry no level and keep
 * running (and billing) Low, so an ongoing conversation never changes model or price mid-way.
 * New rooms never rely on it: they are stamped `DEFAULT_NEW_ROOM_LEVEL` at creation.
 */
export const DEFAULT_CONVERSATION_LEVEL: ConversationLevel = 'low';
/**
 * The level stamped explicitly on every new room (composer, Home chat, compose modal, Slack) when
 * the creator sent none, and the composer's preselection. Owner 2026-10-15: Medium. Guest-chat
 * rooms are excluded: owner-paid with no picker, they stay Low.
 */
export const DEFAULT_NEW_ROOM_LEVEL: ConversationLevel = 'medium';

/**
 * The option each level runs on. Every value must be a listed `CONVERSATION_MODELS` entry.
 * Owner 2026-10-15 (Google levels): Low = Luna, Medium = Flash, High = Argon.
 */
export const CONVERSATION_LEVEL_MODEL: Record<ConversationLevel, ConversationModel> = {
  low: 'luna',
  medium: 'flash',
  high: 'argon',
};

/** Credit multiplier per level, applied to the room's base rate (`conversationMessageCost`). */
export const CONVERSATION_LEVEL_CREDIT_MULTIPLIER: Record<ConversationLevel, number> = {
  low: 1,
  medium: 2,
  high: 4,
};

/** Credit multiplier of an advanced pick, by its picker group (`CONVERSATION_MODEL_TIER`). */
export const CONVERSATION_TIER_CREDIT_MULTIPLIER: Record<ConversationModelTier, number> = {
  low: 1,
  medium: 2,
  high: 4,
  blend: 4,
};

/**
 * Per-room experiment switches (platform eval plan, section 4; owner decision D1).
 * Closed value sets. Set at room create only, by an owner entitled to
 * `FeatureFlag.ROOM_EXPERIMENTS` (internal only); dropped with a warn otherwise and
 * rejected on PATCH. Frozen on each turn snapshot and forwarded on
 * `RoomChatRequestPayload.experiment`. python-api mirrors the values as `Literal`s
 * on `RoomChatRequest.experiment`; the parity fixture
 * `apps/backend/test/fixtures/room-experiment-parity.json` binds both sides.
 * An absent key (or an absent `experiment`) is the product default.
 */
export const ROOM_EXPERIMENT_KEYS = ['gutLines', 'proseVoice', 'roomDirector', 'speakerPick'] as const;
export type RoomExperimentKey = (typeof ROOM_EXPERIMENT_KEYS)[number];
/**
 * `gutLines`: on or off; absent is off (owner 2026-10-16: gut lines are off by default, `on` is an
 * eval arm). `proseVoice`: accepted and recorded only (the prose voice left the product).
 */
export const ROOM_EXPERIMENT_SWITCH_VALUES = ['on', 'off'] as const;
export type RoomExperimentSwitch = (typeof ROOM_EXPERIMENT_SWITCH_VALUES)[number];
/** `roomDirector`: `default` follows the org flag; `on` / `off` override it for this room. */
export const ROOM_DIRECTOR_EXPERIMENT_VALUES = ['default', 'on', 'off'] as const;
export type RoomDirectorExperiment = (typeof ROOM_DIRECTOR_EXPERIMENT_VALUES)[number];
/**
 * `speakerPick` (quick pick plan, 2026-10-16; default flipped by fast first reply Step 9):
 * `quick` (or absent) is the product behavior, one small pick call chooses the first speaker on an
 * untagged turn while the planner casts the rest in parallel; `bids` keeps the old path, where the
 * planner casts the first teller from every bid.
 */
export const ROOM_SPEAKER_PICK_VALUES = ['bids', 'quick'] as const;
export type RoomSpeakerPick = (typeof ROOM_SPEAKER_PICK_VALUES)[number];

export interface RoomExperimentSettings {
  /** On: python's entity perception adds gut / fit stance lines. Absent or off (the default): none. */
  gutLines?: RoomExperimentSwitch;
  /** Off: Nest prepares the room's respondent contexts without the prose-preference source. */
  proseVoice?: RoomExperimentSwitch;
  /**
   * Overrides the resolved `ROOM_DIRECTOR_ENABLED` flag for this room (director plan step 7).
   * `off` disables the director entirely for the room, including any cross-room state:
   * the director must neither read nor write `lastRoomStyle` (or any other carried-over
   * style) for this room, so an off arm never inherits from or leaks into another room.
   * `on` runs it regardless of the org flag; `default` (or absent) follows the flag.
   * Never alters the teller count or casting inputs: arms differ only in the switched behavior.
   */
  roomDirector?: RoomDirectorExperiment;
  /**
   * `quick`: on an untagged, non-@all group turn, python's quick pick chooses the first speaker
   * from a short card per twin and the recent turns; that twin writes at once, without a bid or the
   * planner. A pick failure or timeout runs the bids path for that turn. `bids` or absent: today.
   */
  speakerPick?: RoomSpeakerPick;
}

const ROOM_EXPERIMENT_ALLOWED: Record<RoomExperimentKey, ReadonlyArray<string>> = {
  gutLines: ROOM_EXPERIMENT_SWITCH_VALUES,
  proseVoice: ROOM_EXPERIMENT_SWITCH_VALUES,
  roomDirector: ROOM_DIRECTOR_EXPERIMENT_VALUES,
  speakerPick: ROOM_SPEAKER_PICK_VALUES,
};

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Strict (create validation): a plain object whose every key is known and every value listed. */
export function isRoomExperimentSettings(value: unknown): value is RoomExperimentSettings {
  if (!isPlainRecord(value)) {
    return false;
  }
  return Object.entries(value).every(([key, switchValue]) => {
    if (!Object.prototype.hasOwnProperty.call(ROOM_EXPERIMENT_ALLOWED, key)) {
      return false;
    }
    const allowed = ROOM_EXPERIMENT_ALLOWED[key as RoomExperimentKey];
    return typeof switchValue === 'string' && allowed.includes(switchValue);
  });
}

/**
 * Lenient (read, sanitizer, turn snapshot): listed keys with listed values, in
 * `ROOM_EXPERIMENT_KEYS` order (byte-stable); anything else drops. Undefined when
 * nothing survives, so a room without switches carries no key.
 */
export function toRoomExperimentSettings(value: unknown): RoomExperimentSettings | undefined {
  if (!isPlainRecord(value)) {
    return undefined;
  }
  const kept: Record<string, string> = {};
  for (const key of ROOM_EXPERIMENT_KEYS) {
    const switchValue = value[key];
    if (typeof switchValue === 'string' && ROOM_EXPERIMENT_ALLOWED[key].includes(switchValue)) {
      kept[key] = switchValue;
    }
  }
  return Object.keys(kept).length > 0 ? (kept as RoomExperimentSettings) : undefined;
}

/** Discriminated metadata for system_event messages with event: 'introduction'. */
export interface IntroductionEventMetadata {
  event: 'introduction';
}

/** Type guard for introduction metadata. */
export function isIntroductionMessage(
  metadataType: ChatRoomMetadataType | null,
  metadata: unknown,
): metadata is IntroductionEventMetadata {
  return (
    metadataType === 'system_event' &&
    metadata != null &&
    typeof metadata === 'object' &&
    (metadata as Record<string, unknown>).event === 'introduction'
  );
}

// ---------------------------------------------------------------------------
// Room Settings
// ---------------------------------------------------------------------------

export type RoomVisibility = 'private' | 'org';

/**
 * RND-2580: internal bookkeeping for auto-generated conversation titles.
 * Lives inside `settings` (JSONB) so no schema migration is required.
 */
export interface ChatRoomTitleGenState {
  /** USER-message count at the last title generation — the cadence anchor. */
  atCount?: number;
  /** Hash of the last-generated conversation window — enables skip-if-unchanged. */
  hash?: string;
  /** True once the user manually renames; auto-generation must never overwrite it. */
  locked?: boolean;
  /** Number of successful auto-generations so far — drives the escalating cadence. */
  genCount?: number;
}

/**
 * A single persistent room stimulus (a landing-page/creative version shown to the
 * whole focus group). Transport is the RND-2644 storage-key + inlining path:
 * the image is uploaded once and referenced by `storageKey`; the backend resolves
 * the private key to an inline base64 data URL on every twin turn. `dataUrl` is a
 * bounded fallback (per-image ≤ 1.5 MB) for callers that cannot pre-upload.
 * Experiment-report creatives may use `imagesObjectKey` (FAIRGEN_DT_IMAGES_BUCKETS).
 */
export interface StimulusImage {
  /** Human-readable version label shown to twins (e.g. "Page A"). */
  label: string;
  /** Stable study identity; server supplied from frozen asset references. */
  assetId?: string;
  /** GCS storage key (RND-2644). Preferred transport — backend inlines to base64 per turn. */
  storageKey?: string;
  /** Bounded inline fallback (base64 data URL, per-image ≤ 1.5 MB) used only when no `storageKey`. */
  dataUrl?: string;
  /**
   * Object key in the images bucket (`FAIRGEN_DT_IMAGES_BUCKETS`).
   * Server-injected only from study survey grounding — clients must not supply this
   * (rejected by create/update settings validation).
   */
  imagesObjectKey?: string;
  /**
   * Server-injected from study grounding (Step 3b): the study shows this asset
   * as a sliced landing page, so the room slices it the same way.
   */
  presentation?: 'landing_page';
}

/** Available permitted pixels are supplied on every turn within the media policy. */
export type StimulusImagePolicy = 'always';

/**
 * Room-level creative catalog for study-backed twin chat. Text is always injected
 * into the twin prompt; images ride `settings.stimulusImages` separately.
 */
export interface StudyCreative {
  assetId: string;
  /** Respondent-facing label matching survey language (e.g. "Ad 1"). */
  label: string;
  title?: string;
  description?: string;
  /** Optional Argo visual summary (LEGIBLE TEXT / NOT LEGIBLE). */
  visualSummary?: string;
}

export interface ChatRoomSettings {
  topics?: string;
  topicContext?: string;
  language: string;
  maxParticipants: number;
  /** @deprecated Never read after Step 10 (D3). Removed from the type with the frontend creators in Step 11b. */
  turnDepth?: number;
  /** Persona tokens of the twins that bid each round. Missing key = every participant (default-filled at read). */
  activeParticipants?: Array<string>;
  /** Teller slots per round. Missing = DEFAULT_TELLER_COUNT. Clamped at read to [1, max(1, active count)]. */
  tellerCount?: number;
  sampleEnabled: boolean;
  /**
   * Knowledge Base MVP (Decision 11) — study opt-out for document grounding.
   * Default true (whitelisted in `sanitizeRoomSettings`). When false, orchestration
   * skips the audience-context store merge so a study can opt out of grounding.
   */
  knowledgeEnabled?: boolean;
  visibility: RoomVisibility;
  /** RND-2580: auto-title bookkeeping (not user-configurable). */
  titleGen?: ChatRoomTitleGenState;
  /**
   * Focus-Group Grounding Redesign — persistent room stimuli (≤ 3, the
   * QS_MAX_VERSIONS cap). Re-injected as `input_image` parts on every twin turn
   * so the group can genuinely review the version each twin had not personally seen.
   */
  stimulusImages?: Array<StimulusImage>;
  /**
   * Available permitted pixels are forwarded every turn. Omitted means always.
   */
  stimulusImagePolicy?: StimulusImagePolicy;
  /**
   * The model option twins answer with in this room. Missing = `DEFAULT_CONVERSATION_MODEL`.
   * Set only by an entitled owner (`isConversationModelEnabled`, 403 otherwise); a stored
   * option on a room whose owner lost access runs on the default.
   */
  conversationModel?: ConversationModel;
  /**
   * The conversation level (Low / Medium / High) twins answer at. Missing = `DEFAULT_CONVERSATION_LEVEL`
   * (Low); new rooms are stamped `DEFAULT_NEW_ROOM_LEVEL`. Any room owner may set it. A present
   * `conversationModel` (an advanced pick by an entitled owner) wins over it; a level PATCH clears
   * that key server-side.
   */
  conversationLevel?: ConversationLevel;
  /**
   * Per-room experiment switches (internal owners only, create only). Returned on the
   * room DTO only to the owner. Absent = product default.
   */
  experiment?: RoomExperimentSettings;
}

// Built at runtime: a `u`-flag literal fails the frontend's lower compile target (TS1501).
const MENTIONS_ALL_PATTERN = new RegExp('(^|[^\\p{L}\\p{N}_])@all(?![\\p{L}\\p{N}_])', 'iu');

/**
 * True when a message addresses every twin (`@all`, D4): every twin becomes active for that send.
 * The only text parse left; Nest applies it at acceptance and the composer lets such a send through
 * when every twin is off.
 */
export function mentionsAll(content: string): boolean {
  return MENTIONS_ALL_PATTERN.test(content);
}

/** Room-level teller clamp at read (sanitizeRoomSettings): [1, max(1, active count)], default DEFAULT_TELLER_COUNT. */
export function clampRoomTellerCount(tellerCount: number | undefined, activeCount: number): number {
  const requested = Number.isInteger(tellerCount) ? (tellerCount as number) : DEFAULT_TELLER_COUNT;
  return Math.max(1, Math.min(requested, Math.max(1, activeCount)));
}

/**
 * Turn-level teller clamp at acceptance. A tagged turn (picks > 0) tells with its picks only,
 * the room count ignored: max(1, min(pickCount, bidderCount)) (owner 2026-10-14). Untagged:
 * max(1, min(roomTellerCount, bidderCount)).
 */
export function clampTurnTellerCount(roomTellerCount: number, pickCount: number, bidderCount: number): number {
  if (pickCount > 0) {
    return Math.max(1, Math.min(pickCount, bidderCount));
  }
  return Math.max(1, Math.min(roomTellerCount, bidderCount));
}

export const DEFAULT_ROOM_SETTINGS: ChatRoomSettings = {
  language: 'en',
  maxParticipants: 5,
  sampleEnabled: false,
  visibility: 'private',
};

// ---------------------------------------------------------------------------
// Study Context (survey-grounded twin chat)
// ---------------------------------------------------------------------------

/** Safe room display metadata. Saved answers are never part of a room DTO. */
export interface StudyContext {
  experimentId?: string;
  /**
   * Room-level creatives from the study survey (`assetContext` ± visual summaries).
   * Injected into every twin prompt as text grounding; not sliced per participant.
   */
  creatives?: Array<StudyCreative>;
}

/**
 * The study metadata a room DTO carries: the wire {@link StudyContext} plus
 * display-only choice labels. Labels never travel to Python, so the
 * conversation contract (`StudyContext`) stays unchanged.
 */
export interface ChatRoomStudyContext extends StudyContext {
  /**
   * Per question (name and title), the choice text SurveyJS resolved.
   * Structured maps keep matrix columns and nested questions apart.
   * Older rooms may still store a flat value→text map. Absent on rooms
   * created before this field. Readers that lack a label show the raw value.
   */
  surveyChoiceLabels?: SurveyChoiceLabelMap;
}

/** Server-bound personal records, delivered only to the selected participant. */
export interface PersonalStudyContext {
  /** @minLength 1 */
  experimentId: string;
  /** @minLength 1 */
  personaId: string;
  sourceRespondentId: string | null;
  availability: PersonalRecordAvailability;
  surveyAnswers: Record<string, unknown> | null;
  surveyQuestions: Record<string, string> | null;
}

/** Private persistence, distinct from the safe room metadata and individual wire slice. */
export interface StoredStudyContext extends ChatRoomStudyContext {
  participants: Record<string, PersonalStudyContext>;
  /** Authorized frozen study references, never returned in a room DTO. */
  stimulusImages?: Array<StimulusImage>;
}

// ---------------------------------------------------------------------------
// REST DTOs
// ---------------------------------------------------------------------------

/** QS-123 — room discussion mode. 'debate' selects the adversarial prompt on the
 *  Python side; 'focus_group' / 'interview' and an omitted value all keep the
 *  collaborative group-discussion default (byte-identical to before). */
export type RoomDiscussionType = 'focus_group' | 'interview' | 'debate';

export interface CreateChatRoomDto {
  /** Population UUID token (NOT the DB integer id). Backend resolves token → int before insert. */
  populationId: string;
  /** Persona UUID tokens for the participants to invite to the room. */
  participantPersonaIds: Array<string>;
  /** Room settings. Backend merges with DEFAULT_ROOM_SETTINGS for any missing fields. */
  settings?: Partial<ChatRoomSettings>;
  /** When created from a study report, identifies the authorized saved study. */
  studyContext?: StudyContext;
  /** Per-participant AI filter tags captured at selection time. Keyed by persona token. */
  participantTags?: Record<string, Array<{ chip: string; displayQuestion: string; displayAnswer: string }>>;
  /** Caller-supplied portrait URLs captured at selection time. Keyed by persona token. */
  participantImages?: Record<string, string>;
  /** Caller-supplied audience bios captured at selection time. Keyed by persona token. */
  participantBios?: Record<string, string>;
  /**
   * Focus-Group Grounding Redesign — per-twin grounding text (personaId → grounding).
   * Snapshotted into settings JSONB at create (`_participantGrounding`) and forwarded
   * per turn; Python appends it to each persona's prompt. ≤ ~400 chars/twin.
   */
  participantGrounding?: Record<string, string>;
  /**
   * Knowledge Base MVP — per-twin knowledge briefing (personaId → briefing text) derived
   * from the audience's knowledge documents at focus-group launch. Snapshotted into settings
   * JSONB (`_participantKnowledge`) and forwarded per turn beside `participantGrounding`;
   * Python injects it after the grounding block, before `latestStance`. ≤ ~1200 chars/twin.
   */
  participantKnowledge?: Record<string, string>;
  /**
   * Knowledge Base MVP (per-study scoping) — the Quick Study clientSessionId (`qs-<ms>`)
   * whose knowledge documents ground this room. Snapshotted into settings JSONB
   * (`_knowledgeStudyId`) at create; orchestration resolves the per-study, creator-scoped
   * store instead of the population-level one, so studies on a shared audience never ground
   * on each other's docs. Absent → population-level grounding, unchanged. Pattern
   * `^[A-Za-z0-9_-]{1,64}$`.
   */
  knowledgeStudyId?: string;
  /** QS-123 — discussion mode for the room; 'debate' makes the twins argue opposing sides. */
  roomType?: RoomDiscussionType;
}

export interface ChatRoomDto {
  /** Public id: the room token (never the DB row id). */
  id: string;
  token: string;
  status: ChatRoomStatus;
  title: string | null;
  populationId: string | null;
  populationName: string | null;
  settings: ChatRoomSettings;
  /** Present when the room was created from a study report. */
  studyContext?: ChatRoomStudyContext | null;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  participants: Array<ChatRoomParticipantDto>;
  /** Step 10c: what the room stores (single-room reads; absent on list items). */
  materials?: Array<ChatRoomMaterial>;
  /**
   * True only when the requesting user owns the room. Org collaborators, guests and
   * shared views get false (rename is owner-only). Absent is read as false.
   */
  viewerIsOwner?: boolean;
  /**
   * The room's suggested opening question (D8), in the room language. Absent until
   * generated, when the room had nothing to build it on, and ignored once the room has a message.
   */
  openingQuestion?: string;
  /**
   * Credits per message at the room's base rate (audience rate, before the level multiplier).
   * The composer previews `conversationMessageCost(creditsPerMessage, multiplier)`. Set on
   * single-room reads, create and settings updates; absent on list items.
   */
  creditsPerMessage?: number;
  /**
   * What the room's next message runs on and costs, resolved from the OWNER's gates with the
   * same logic as the charge (so non-owners render the owner's level and price). `level` is the
   * stored level (Low when none applies); `advancedModel` is set only when an advanced pick
   * applies. `creditsPerMessage` = base rate × `multiplier`. Set alongside `creditsPerMessage`.
   */
  effectiveConversation?: ChatRoomEffectiveConversation;
  /**
   * Platform eval readiness placeholder: the room director's preparation state per
   * participant persona token, for an eval driver to poll before turn 1. Owner only
   * (internal rooms). Absent when the director is off for the room or not available
   * (always absent until the director lands; the director branch fills it).
   * Participants' own readiness is `participants[].enrichmentStatus`.
   */
  directorReadiness?: Record<string, RoomDirectorReadinessState>;
  /**
   * Platform eval readiness: true once the room director is ready for turn 1 (every entry of
   * `directorReadiness` ready). Owner only. Absent (or false) when the director is off for the
   * room or not available; always absent until the director lands (the director branch sets it).
   */
  directorReady?: boolean;
}

/** Closed states of `ChatRoomDto.directorReadiness` entries. */
export const ROOM_DIRECTOR_READINESS_STATES = ['pending', 'ready', 'failed'] as const;
export type RoomDirectorReadinessState = (typeof ROOM_DIRECTOR_READINESS_STATES)[number];

/** The room's resolved conversation as the room DTO exposes it (see `ChatRoomDto.effectiveConversation`). */
export interface ChatRoomEffectiveConversation {
  level: ConversationLevel;
  advancedModel: ConversationModel | null;
  multiplier: number;
  creditsPerMessage: number;
}

export interface ChatRoomParticipantDto {
  /** Public id: the persona id, unique per room (never the DB row id). */
  id: string;
  personaId: string;
  featuredTwinId: string | null;
  displayName: string;
  tagline: string | null;
  imageUrl: string | null;
  enrichmentStatus: ChatRoomParticipantEnrichmentStatus;
  stances: Record<string, string> | null;
  joinedAt: string;
  conversationStarters?: Array<string>;
  selectionTags?: Array<{ chip: string; displayQuestion: string; displayAnswer: string }>;
  bio?: string;
  highlights?: Array<string>;
  audienceTagNames?: Array<string>;
  /**
   * Quick Study only — the twin's AUTHORED subsegment labels (`Persona.segment_labels`;
   * the draw IS the membership), the same vocabulary the Compare-segments picker shows.
   * Distinct from `audienceTagNames` (Conversations' attribute-matched tags). Rendered as
   * chips AFTER the bio in `ParticipantPopover`; absent/[] for every non-Quick-Study caller.
   */
  subsegmentLabels?: Array<string>;
  /**
   * Knowledge Base MVP — the per-twin knowledge briefing derived from the audience's
   * knowledge documents (populated by `applyParticipantKnowledge` from the room's
   * `_participantKnowledge` snapshot). Rendered in `ParticipantPopover` under the bio;
   * absent when the twin had no knowledge match or the study has no documents.
   */
  knowledgeBriefing?: string;
}

/**
 * Message DTO. `metadata` is discriminated by `metadataType`:
 *   - `'sample_artifact'` → metadata is `SampleArtifactMetadata`
 *   - `'system_event'`    → metadata is `Record<string, unknown>`
 *   - `null`              → metadata is `null`
 */
export interface ChatRoomMessageDto {
  id: string;
  /** The room token. */
  roomId: string;
  sequenceIndex: number;
  /** The sender's persona id (matches `ChatRoomParticipantDto.id`), or null. */
  senderParticipantId: string | null;
  senderType: ChatRoomSenderType;
  content: string;
  mode: ChatRoomMessageMode | null;
  metadataType: ChatRoomMetadataType | null;
  metadata: SampleArtifactMetadata | Record<string, unknown> | null;
  images?: Array<{ base64: string; mimeType: string }>;
  createdAt: string;
  imageKeys?: Array<string>;
  /** Hosted videos attached to a user message (RND-2407). Absent when none. */
  videoRefs?: Array<{ kind: 'video'; url: string; label?: string; mimeType?: string }>;
  /** PDFs sent with a user message (Step 10c). Absent when none. */
  documentRefs?: Array<ChatMessageDocumentRef>;
  /** User messages that opened a turn: what that turn ran with, from its frozen snapshot. */
  turnStatus?: ChatRoomTurnStatus;
}

/** Why a turn ran at the default conversation rather than the room's stored choice. */
export type RoomTurnConversationFallback = 'entitlement_lookup_failed' | 'resolve_failed';

/**
 * Read-only per-turn status on the user message that opened the turn (platform eval
 * plan, Step 3). Persona tokens and closed values only, never row ids. Degraded
 * stages are on each twin reply's `metadata.degraded`; a bidder with no reply, no
 * failure note and no reaction for the turn stayed silent.
 */
export interface ChatRoomTurnStatus {
  /** Resolved level; null on an advanced pick or a turn frozen before levels shipped. */
  conversationLevel: ConversationLevel | null;
  /** Null on a turn frozen before levels shipped. */
  conversationSource: 'level' | 'advanced' | 'default' | null;
  /** Present only when the turn ran at the default because the choice could not be resolved. */
  conversationFallback?: RoomTurnConversationFallback;
  /** Twins that bid this turn, persona tokens, room order. */
  bidderPersonaIds: Array<string>;
  /** Teller slots this turn; null on solo rooms and ask-about turns. */
  tellerCount: number | null;
}

/**
 * Step 10c: a PDF the user sent with a message, persisted under
 * `chat-rooms/{token}/documents/`. The text the twins receive is unchanged
 * (the composer's inlined text in `content`).
 */
export interface ChatMessageDocumentRef {
  /** Storage key, readable through the room materials route. */
  key: string;
  /** File name as sent. */
  name: string;
  mimeType: string;
}

/** Kinds of room material (Step 10c, SPEC section 8 C). */
export const ROOM_MATERIAL_KINDS = ['image', 'video', 'document'] as const;
export type RoomMaterialKind = (typeof ROOM_MATERIAL_KINDS)[number];

/**
 * One thing the room stores (Step 10c), derived at read from room stimuli and
 * the message blob, deduplicated by key, room stimuli first then send order.
 * Clients extend the list from the message broadcast (no socket event).
 */
export interface ChatRoomMaterial {
  kind: RoomMaterialKind;
  /**
   * Read key for the room materials route (`GET /chat-rooms/:token/materials/*key`,
   * or its guest twin): the storage key, or `stimulus:<n>` for an inline room
   * stimulus. Null for a hosted video (see `url`).
   */
  key: string | null;
  /** Hosted URL (videos). Null otherwise. */
  url: string | null;
  /** Room stimulus label, video label or document name. Null for a sent image. */
  label: string | null;
  mimeType: string | null;
  /** The message that carried it; null for a room stimulus. */
  messageSequenceIndex: number | null;
}

export interface ChatRoomListItemDto {
  id: string;
  token: string;
  status: ChatRoomStatus;
  title: string | null;
  populationName: string | null;
  participantCount: number;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  participantImages: Array<string>;
}

// ---------------------------------------------------------------------------
// Unified Chat History (rooms + 1:1 conversations)
// ---------------------------------------------------------------------------

export type ConversationEntryType = 'room' | 'one_on_one';

export interface ConversationHistoryEntry {
  type: ConversationEntryType;
  id: string;
  title: string;
  subtitle: string | null;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  participantImages: Array<string>;
  participantCount: number;
  status: 'active' | 'closed' | 'preparing' | 'error';
  href: string;
}

export interface SendChatRoomMessageDto {
  /** Stable across retries of the same logical send; unique for an edited send. */
  clientRequestId?: string;
  content: string;
  mode: ChatRoomMessageMode;
  images?: Array<{ base64: string; mimeType: string }>;
  /** A landing-page URL captured server-side and attached as a screenshot. */
  captureUrl?: string;
  /** Hosted video creatives the twins WATCH before replying (max 3). */
  stimulusVideos?: Array<{ url: string; label?: string; mimeType?: string }>;
  /** Persona tokens picked as tellers for this send (circle teller state or @tag). Max 10, unique. */
  tellerPicks?: Array<string>;
}

// ---------------------------------------------------------------------------
// Socket.IO event payloads — namespace: /chat
// ---------------------------------------------------------------------------

export interface ChatRoomParticipantReadyEvent {
  roomId: string;
  participantId: string;
  displayName: string;
  imageUrl: string;
  featuredTwinId: string | null;
  bio?: string | null;
  tagline?: string | null;
  audienceTagNames?: Array<string>;
}

/** Lightweight event for progressive image loading (avatar swap from initials). */
export interface ChatRoomParticipantImageEvent {
  roomId: string;
  participantId: string;
  imageUrl: string;
}

/** `opening-question`: the room's suggested opening question, once generated (D8). */
export interface ChatRoomOpeningQuestionEvent {
  roomId: string;
  question: string;
}

export interface ChatRoomReadyEvent {
  roomId: string;
  status: 'active';
}

export interface ChatRoomMessageEvent {
  roomId: string;
  message: ChatRoomMessageDto;
}

export type ChatRoomParticipantState = 'idle' | 'thinking' | 'speaking';

export interface ChatRoomParticipantStateEvent {
  /** Identifies the generation turn so delayed delivery cannot overwrite a newer turn. */
  turnId?: string;
  roomId: string;
  participantId: string;
  state: ChatRoomParticipantState;
  /**
   * True only on the idle that follows the participant's own callback for `turnId` (a message, a
   * reaction-only or silent reactor, or a failure): nothing more comes from it this turn, so a later
   * `thinking` for the same turn is a reordered event. A cast's wave-1 idle is not final.
   */
  final?: boolean;
}

export interface ChatRoomTurnCompleteEvent {
  /** Optional for compatibility with servers predating durable callback delivery. */
  turnId?: string;
  roomId: string;
}

/** RND-2580: pushed when a conversation's title is (re)generated or renamed. */
export interface ChatRoomTitleUpdatedEvent {
  /** Room token (matches the `roomId` used by other room events). */
  roomId: string;
  title: string;
}

export interface ChatRoomClosedEvent {
  roomId: string;
  title: string | null;
  /** When present, indicates an abnormal close (e.g. 'timeout'). Absence means normal user-initiated close. */
  reason?: 'timeout' | 'error';
}

// ---------------------------------------------------------------------------
// Room reactions
// ---------------------------------------------------------------------------

export const ROOM_REACTION_TARGET_KINDS = ['message', 'turn_question', 'turn_reply', 'turn_display'] as const;
export type RoomReactionTargetKind = (typeof ROOM_REACTION_TARGET_KINDS)[number];

export interface MessageReactionTarget {
  kind: 'message';
  sequenceIndex: number;
}
export interface TurnQuestionReactionTarget {
  kind: 'turn_question';
}
/** participant = the replying twin's persona token. */
export interface TurnReplyReactionTarget {
  kind: 'turn_reply';
  participant: string;
}
export interface TurnDisplayReactionTarget {
  kind: 'turn_display';
}
export type RoomReactionTarget =
  | MessageReactionTarget
  | TurnQuestionReactionTarget
  | TurnReplyReactionTarget
  | TurnDisplayReactionTarget;

export interface RoomReactionItem {
  target: RoomReactionTarget;
  /** One whole fully-qualified RGI sequence from the D15 list (exact lookup). */
  emoji: string;
}

/** A reaction from the persisted window, resolved by Nest. Only resolved reactions are sent. */
export interface RoomWindowReaction {
  /** Reactor persona token. */
  by: string;
  sequenceIndex: number;
  emoji: string;
  /** ISO 8601; Python orders REACTIONS SO FAR by (at, by). */
  at: string;
}

/** Wire entry: resolved reactions only. */
export interface ChatRoomReactionDto {
  /** sequenceIndex of the resolved target message (ChatRoomMessageDto.id === String(sequenceIndex)). */
  messageSequenceIndex: number;
  emoji: string;
  /** Reactor persona token (matches ChatRoomParticipantDto.personaId). */
  by: string;
  turnId: string;
  at: string;
}

/** Socket.IO /chat event 'message-reactions' (full snapshot) and the GET body. */
export interface ChatRoomReactionsEvent {
  /** Room token, like every other room event. */
  roomId: string;
  revision: number;
  reactions: Array<ChatRoomReactionDto>;
}

// ---------------------------------------------------------------------------
// Room display (audience_plot)
// ---------------------------------------------------------------------------

/** Where the display reads from (D13, D13a). Python never receives a URI. */
export interface RoomAudienceDisplaySource {
  /** Population token of the room's own audience. Python calls Nest's room-scoped internal routes with roomId + this token. */
  populationId: string;
}

export interface AudiencePlotView {
  /** Validated by slide_contracts.plot_payload_errors in Python; carries plot. */
  chart: AnalystChartSpec;
  base: { n: number };
}

/** Where a room display reads from. python-api mirrors it as `DisplaySource`. */
export const ROOM_DISPLAY_SOURCES = ['audience'] as const;
export type RoomDisplaySource = (typeof ROOM_DISPLAY_SOURCES)[number];

/** metadata of a metadataType 'audience_plot' message (plus responseTurnId / responseParticipantId). */
export interface AudiencePlotMetadata {
  /** Audience detail column key. */
  column: string;
  /** Column label, used in the deterministic caption with base.n. */
  label: string;
  chartKind: AudienceChartKind;
  /** Whole original audience, unweighted, min cell 8 with folding into Other. */
  chart: AnalystChartSpec;
  base: { n: number };
  /** Same column over the room's twins only, counts, no min-cell floor. Absent when it cannot be built. */
  roomPlot?: AudiencePlotView;
  /** Which source the chart came from. A missing value (older rows) reads as `audience`. */
  source?: RoomDisplaySource;
  /** True when the user asked for the chart. A missing value reads as false. */
  requested?: boolean;
}

/** One chart already shown in the room, with the group round it appeared in. */
export interface RoomDisplayShown {
  column: string;
  round: number;
  source: RoomDisplaySource;
  requested: boolean;
}

/** Pace facts Nest sends with a display turn: the current group round and the charts already shown. */
export interface RoomDisplayPace {
  round: number;
  shown: Array<RoomDisplayShown>;
}

export interface RoomDisplayPayload {
  /** Persisted as the message content; the one history line twins see (D13). */
  historyLine: string;
  plot: AudiencePlotMetadata;
}

/**
 * One entry of the room detail's `displayColumns` (9b): every audience detail
 * column, taken before the detail's token trim. `key` is the audience-detail
 * column key (its own key space). `chartKind` null = not chartable (never in
 * the D<n> enum). Python caps the enum at 60 columns.
 */
export interface RoomAudienceDetailColumn {
  key: string;
  label: string;
  chartKind: AudienceChartKind | null;
  /**
   * A coded scale's stored option labels in code order (value labels sorted
   * by numeric code). Present on `single` scales only; the chart keeps this
   * order instead of share order, with Other last.
   */
  optionOrder?: Array<string>;
}

/** `audience` = the whole original audience; `room` = the room's twins only (Nest filters). */
export const ROOM_AUDIENCE_ROW_SCOPES = ['audience', 'room'] as const;
export type RoomAudienceRowScope = (typeof ROOM_AUDIENCE_ROW_SCOPES)[number];

/**
 * Rows for the room display (`.../rows?scope=audience|room&keys=...`): the
 * knowledge `AudienceRows` shape. Nest filters the room scope to the room's
 * participants itself; no persona token or id is in this response, and
 * Python never sends one.
 */
export interface RoomAudienceRowsResponse {
  /** Same shape as Nest's knowledge `AudienceRowsColumn` (shared-types cannot import the backend). */
  columns: Array<{ key: string; label: string; category: string | null; type: string | null }>;
  /** Cells are Nest's knowledge `AudienceRowCell`. */
  rows: Array<Record<string, string | Array<string> | number | null>>;
  rowCount: number;
  truncated: boolean;
}

// ---------------------------------------------------------------------------
// Cast callback (Python -> Nest, after the planner validates)
// ---------------------------------------------------------------------------

export interface RoomCastEntry {
  /** Persona token. */
  participant: string;
  role: RoomRole;
}

export interface RoomCastCallbackPayload {
  turnId: string;
  /** Every bidder exactly once, speaking order, tellers first. No bid content, no lines, no relates_to. */
  cast: Array<RoomCastEntry>;
  displayRequested: boolean;
}

// ---------------------------------------------------------------------------
// Python API endpoint contracts
// ---------------------------------------------------------------------------

/** Typed assessment state carried between room turns; Python owns topic meaning. */
export interface ChatTopicKnowledge {
  topic: string;
  level: number;
  kind?: string;
  presented?: boolean;
  reviewed_in_study?: boolean;
}

export interface RoomChatRequestPayload {
  roomId: string;
  mediaPixels?: Record<string, MediaPixelObject>;
  /** Digest -> how those pixels are shown (Step 3b). Absent digests are plain images. */
  mediaPresentation?: Record<string, MediaPresentation>;
  /** Unique ID for this turn, used to reject stale callbacks after a restart. */
  turnId?: string;
  userMessage: string;
  /** Literal persisted message identity for the current question and attachments. */
  userMessageId?: string;
  /**
   * @deprecated There is no conversation mode. Accepted and ignored by Python for
   * one release; Nest no longer sends it.
   */
  conversationStyle?: string;
  language?: string;
  /**
   * The room's conversation model option. Omitted when unset, default, or the owner
   * is no longer entitled, so Python runs today's routes.
   */
  conversationModel?: ConversationModel;
  /**
   * The room's experiment switches, frozen on this turn's snapshot (internal rooms only).
   * Omitted when the room has none, so those turns stay byte-identical.
   */
  experiment?: RoomExperimentSettings;
  topicContext?: string;
  /** QS-123 — 'debate' selects the adversarial discussion prompt on the Python side;
   *  omitted/other values keep the collaborative group-discussion default. */
  roomType?: RoomDiscussionType;
  /** Safe room metadata; private records travel only in selected participant envelopes. */
  studyContext?: StudyContext;
  /**
   * Focus-Group Grounding Redesign — per-twin grounding text (personaId → grounding).
   * Python appends `participantGrounding[personaId]` to the persona prompt (same seam
   * as `latestStance`, but placed BEFORE it since grounding is room-static).
   */
  participantGrounding?: Record<string, string>;
  /**
   * Knowledge Base MVP — per-twin knowledge briefing (personaId → briefing text). Python
   * injects `participantKnowledge[personaId]` immediately AFTER the grounding block and
   * BEFORE `latestStance`, wrapped in anti-determinism framing (population-level tendency,
   * not a personal verdict). Room-static, forwarded every turn like `participantGrounding`.
   */
  participantKnowledge?: Record<string, string>;
  /** Legacy slot retained for explicit rejection by Python. Use participant evidence/mediaPixels. */
  stimulusImages?: Array<{ label: string; url: string }>;
  conversationWindow: Array<{
    role: string;
    content: string;
    senderType?: ChatRoomSenderType;
    personaName?: string;
    /** Stable room-participant identity; disambiguates duplicate display names. */
    participantId?: string;
    /** Persistent transcript position, used by source-linked context records. */
    sequenceIndex?: number;
    /** Legacy slot retained for explicit rejection by Python. Durable media is separate from text history. */
    imageUrls?: string[];
    /** Twin messages only: metadata.roomRole of the persisted message. */
    roomRole?: RoomRole;
  }>;
  participants: Array<{
    participantId: string;
    personaId: string;
    /** The persona_id keying the twin's personas.jsonl row (not a DB row id). Group rounds read the twin's field answers by it (D8). */
    sourcePersonaId?: string;
    personalStudyContext?: PersonalStudyContext;
    mediaEvidence?: Array<ParticipantMediaEvidence>;
    personaName: string;
    personaPrompt: string | null;
    personaSummary: string | null;
    personaNarrative: string | null;
    personaAttributes: Record<string, unknown>;
    personaGrounding?: PersonaGrounding | null;
    enrichedProfile: Record<string, string> | null;
    voiceProfile: Record<string, string> | null;
    columnCategories: Record<string, string> | null;
    aiTags: Array<string> | null;
    latestStance: string | null;
    topicKnowledge?: Array<ChatTopicKnowledge>;
    imageUrl: string | null;
  }>;
  /** Group turns: bidders (the only carrier of the set, row ids). */
  selectedRespondentIds: Array<string>;
  /** Persona tokens; each must be a selected participant's personaId. Default []. */
  tellerPicks?: Array<string>;
  /** Group turns: the turn-level clamped count. Absent on solo and 1:1. */
  tellerCount?: number;
  /**
   * Group turns: true when the moderator tagged twins at acceptance, even if every pick dropped
   * out before the call (rev 3: a tagged round tells with its picks only, one teller when none
   * is left, under the tagged comment cap). Absent = untagged.
   */
  tagged?: boolean;
  /** Group turns: true when the send addressed every twin (`@all`); Python skips its bidder cap. Absent = false. */
  addressAll?: boolean;
  /** Resolved reactions on messages in conversationWindow. Default []. */
  reactions?: Array<RoomWindowReaction>;
  /** Null or absent: no display this turn (one bidder, no audience, or the room has no population). */
  audienceDisplay?: RoomAudienceDisplaySource | null;
  /** Sent only with `audienceDisplay`: the current group round and the charts shown so far. */
  displayPace?: RoomDisplayPace | null;
  fileSearchStoreNames?: Array<string>;
  images?: Array<{ base64: string; mimeType: string }>;
}

export interface RoomChatResponsePayload {
  participantId: string;
  personaName: string;
  /**
   * May be '' only for a reaction-only or silent reactor, or a george item. On a george item it is
   * George's note: beside the chart, or alone when the requested chart cannot be shown.
   */
  response: string;
  stance: string | null;
  topicKnowledge: Array<ChatTopicKnowledge>;
  /** Echoed back from the request so the backend can reject stale callbacks. */
  turnId?: string;
  /** Stable machine-readable failure surfaced when a participant could not answer. */
  error?: 'generation_failed';
  /** How much of the final answer rests on the twin's own profile records. */
  answerGrounding?: AnswerGroundingLevel | null;
  /** Group path only. Kept reactions after validation. Default []. */
  reactions?: Array<RoomReactionItem>;
  /** Group path only, on success and on failure items. Absent on solo, 1:1, display. */
  roomRole?: RoomRole | null;
  /** Group path only: what a reactor's text responds to. Nest resolves it to `metadata.replyTo` (decision M). */
  replyTo?: RoomCallbackReplyTo | null;
  /**
   * Group path only: a reactor that chose silence (empty response, no reactions). Valid: no row, the twin completes.
   * On a george item with no display and no note: the chart the cast asked for was withdrawn; Nest clears
   * George, no row.
   */
  silent?: boolean;
  /** Only on the display item (participantId === GEORGE_PARTICIPANT_ID). */
  display?: RoomDisplayPayload | null;
  /** The fallbacks python took producing this reply (`services/turn_degradation`); omitted when none. */
  degraded?: Array<{ stage: string; kind: string }>;
  /** Where this reply's stance came from (position_turn). */
  stanceSource?: 'detected' | 'estimated' | 'reused' | 'free';
  /** Python could not audit this reply. */
  auditUnavailable?: boolean;
}

export interface RoomSampleRequestPayload {
  roomId: string;
  userQuestion: string;
  populationId: string;
  personasJsonlUri: string;
  personaSelectionUri: string;
}

export interface RoomSampleResultPayload {
  quantResults: {
    questionUsed: Record<string, unknown>;
    answers: Array<{ option: string; count: number; percentage: number }>;
    sampleSize: number;
  };
  chartData: {
    chartType: 'bar' | 'rating_distribution' | 'boolean_split';
    labels: Array<string>;
    values: Array<number>;
    percentages: Array<number>;
  };
}

// ---------------------------------------------------------------------------
// Sample artifact metadata (stored in chat_room_messages.metadata)
// ---------------------------------------------------------------------------

export interface SampleArtifactMetadata {
  chartType: 'bar' | 'rating_distribution' | 'boolean_split';
  chartData: {
    labels: Array<string>;
    values: Array<number>;
    percentages: Array<number>;
  };
  keyInsights: Array<string>;
  sampleSize: number;
  questionUsed: string;
}
